import { TemplateFeatureToggles, TemplateOpeningRoutes } from "@/modules/template/common/enum";
import { useGetTransactionTypesQuery } from "@/modules/transaction/services";
import { type CloudflareAsset, SnackbarAlertTypes } from "@/shared/common/enums";
import { CloudFlareImage, EmptyOrErrorContent, Notification } from "@/shared/components";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import { setAlertNotification } from "@/shared/state/notification";
import type { IAlertNotification } from "@/shared/state/notification/notificationSlice";
import { Radio } from "@ab.uitools/ui-library";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedTransactionType: string;
  setSelectedTransactionType: Dispatch<SetStateAction<string>>;
}

export const SelectTransactionTypeModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedTransactionType,
  setSelectedTransactionType,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { toggles } = useAppSelector((state) => state.featureToggle);

  const {
    data: transactionTypesList,
  } = useGetTransactionTypesQuery({}, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data, isFetching, error, isError }) => ({
      isFetching,
      isError,
      error,
      data: data?.filter((transactionType) => {
        return toggles?.[TemplateFeatureToggles[transactionType as keyof typeof TemplateFeatureToggles]] &&
          !!TemplateOpeningRoutes[transactionType];
      }),
    })
  });

  const onContinue = () => {
    if (!selectedTransactionType) {
      dispatch(setAlertNotification({
        type: SnackbarAlertTypes.Error,
        message: "template.selectTransctionType.selectTypeError",
      }));
      return;
    }
    onConfirm();
  };

  const onSelectTransactionType = (type: string) => {
    setSelectedTransactionType(type);
    dispatch(setAlertNotification({} as IAlertNotification));
  };

  const handleClose = () => {
    onClose();
    setSelectedTransactionType("");
  };

  useEffect(() => {
    if (isOpen && selectedTransactionType) {
      setSelectedTransactionType("");
    }
  }, [isOpen]);

  return (
    <Modal
      closeIcon
      titleProps={{
        title: t("template.selectTransctionType.title"),
        size: "large"
      }}
      subtitle={t("template.selectTransctionType.subtitle")}
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOutsideClick={false}
      onSubmit={() => {}}
      className="select-template-type-dialog"
      buttonProps={{
        confirm: {
          buttonText: t("general.continue"),
          disabled: !transactionTypesList?.length,
          onClick: onContinue
        },
        cancel: {
          buttonText: t("general.cancel")
        },
      }}
    >
      <Notification className="mb-24"/>
      <div className="widget full-width">
        <div className="widget-content">
          {!transactionTypesList?.length && (
            <div className="p-40">
              <EmptyOrErrorContent
                isShowButton={false}
                title={t("template.selectTransctionType.emptyTitle")}
              />
            </div>
          )}
          {transactionTypesList?.map((transactionType, index) => (
            <div
              onClick={() => onSelectTransactionType(transactionType)}
              key={transactionType}
              className="flexbox align-items--center full-width pl-20 pr-20 cursor-pointer"
            >
              <div className="item-icon flexbox align-items--center justify-content--center">
                <div className="flexbox align-items--center justify-content--center">
                  <CloudFlareImage size="small" path={transactionType as CloudflareAsset}/>
                </div>
              </div>
              <div className="flexbox flex-direction--column pl-16 full-width">
                {!!index && (
                  <Divider
                    color="light"
                    type="primary"
                    isHorizontal
                  />
                )}
                <div className="flexbox justify-content--between full-width py-20">
                  <div className="full-width flexbox">
                    <Text className="pr-16 truncate" size="medium" weight="semibold">
                      {t(`banking.transactions.${transactionType}`)}
                    </Text>
                  </div>
                  <Radio isSelected={selectedTransactionType === transactionType}/>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
};
